// store/authStore.ts
import { StateCreator } from 'zustand';
import { loginUser, loginWithSsoTokenAPI } from 'Zustand/services/authService';
import { getTestvisionUserRolesAPI } from 'Zustand/services/projectsService';
import { normalizeUserRoles } from 'Utils/helperFunctions';
import { ApiState } from 'Zustand/types/apiTypes';
import {  LoginResponse } from 'Zustand/types/authTypes';

export interface AuthState {
  auth: ApiState<LoginResponse>;
  token: string | null;
  legalEntityNodeid: string |  null;
  testvisionusername: string;
  msalUsername: string | null;
  // Roles fetched and validated during login, so pages (e.g. the Projects
  // header) can reuse them instead of calling /testvision/userroles again.
  // Not persisted to localStorage — a hard refresh re-fetches on demand.
  userRoles: string[];
  setTestvisionUsername: (testvisionusername: string) => void;
  setMsalUsername: (msalUsername: string | null) => void;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithSso: (account: { username: string }, idToken?: string) => Promise<boolean>;
  logout: () => void;
  restoreAuth: () => void;
}

// A user with no roles isn't allowed in, for either login path.
const NO_ROLES_ERROR =
  'No roles found for this TestVision user. Please contact your administrator.';
const ROLES_FETCH_ERROR = 'Failed to get user roles. Please try again.';

// Fetches and validates TestVision roles for both login paths, distinguishing
// a request failure (network/server error) from a genuinely empty result.
// Returns the roles so the caller can cache them for reuse after login.
const requireUserRoles = async (user: string): Promise<string[]> => {
  let rolesResponse;
  try {
    rolesResponse = await getTestvisionUserRolesAPI(user);
  } catch {
    throw new Error(ROLES_FETCH_ERROR);
  }

  const roles = normalizeUserRoles(rolesResponse);
  if (!roles.length) {
    throw new Error(NO_ROLES_ERROR);
  }

  return roles;
};

export const createAuthSlice: StateCreator<AuthState> = (set, get) => ({
  auth: {
    loading: false,
    error: null,
    data: null
  },
  token: localStorage.getItem("token") ||  null,
  legalEntityNodeid: localStorage.getItem("legalEntityNodeid") ||  null,
  testvisionusername: localStorage.getItem("testvisionusername") || "",
  msalUsername: localStorage.getItem("msalUsername") || null,
  userRoles: [],

  setTestvisionUsername: (testvisionusername) => {
    localStorage.setItem("testvisionusername", testvisionusername);
    set({ testvisionusername });
  },

  setMsalUsername: (msalUsername) => {
    if (msalUsername) {
      localStorage.setItem("msalUsername", msalUsername);
    } else {
      localStorage.removeItem("msalUsername");
    }
    set({ msalUsername });
  },

  login: async (email, password) => {
    set((state) => ({
      auth: { ...state.auth, loading: true, error: null },
    }));
    try {
      const res: LoginResponse = await loginUser(email, password);

      const roles = await requireUserRoles(get().testvisionusername);

      localStorage.setItem('token', res.accesstoken);
      localStorage.setItem('legalEntityNodeid', res.clientnodeid);

      set({
        auth: { loading: false, error: res?.message || null, data: res },
        token: res.accesstoken,
        legalEntityNodeid: res?.clientnodeid || null,
        userRoles: roles,
      });

      return true;
    } catch (err: any) {
      localStorage.removeItem("token");
      localStorage.removeItem("legalEntityNodeid");
      set({
        auth: {
          loading: false,
          error: err.message || 'Login failed',
          data: null,
        },
        token: null,
        legalEntityNodeid: null,
        userRoles: [],
      });
      return false;
    }
  },

  loginWithSso: async (account, idToken) => {
    set((state) => ({
      auth: { ...state.auth, loading: true, error: null },
    }));
    try {
      const roles = await requireUserRoles(get().testvisionusername);

      if (!idToken) {
        throw new Error(
          'SSO login failed: no ID token was returned for this account. Please try again.'
        );
      }

      const res: LoginResponse = await loginWithSsoTokenAPI(idToken);

      localStorage.setItem('token', res.accesstoken);
      localStorage.setItem('legalEntityNodeid', res.clientnodeid);
      localStorage.setItem('msalUsername', account.username);

      set({
        auth: { loading: false, error: res?.message || null, data: res },
        token: res.accesstoken,
        legalEntityNodeid: res?.clientnodeid || null,
        msalUsername: account.username,
        userRoles: roles,
      });

      return true;
    } catch (err: any) {
      localStorage.removeItem('token');
      localStorage.removeItem('legalEntityNodeid');
      localStorage.removeItem('msalUsername');
      set({
        auth: {
          loading: false,
          error: err.message || 'SSO login failed',
          data: null,
        },
        token: null,
        legalEntityNodeid: null,
        msalUsername: null,
        userRoles: [],
      });
      return false;
    }
  },

  logout: () => {
    localStorage.clear();
    set({
      token: null,
      legalEntityNodeid: null,
      testvisionusername: "",
      msalUsername: null,
      userRoles: [],
      auth: { loading: false, error: null, data: null },
    });
  },

  restoreAuth: () => {
    const token = localStorage.getItem('token');
    const testvisionusername = localStorage.getItem("testvisionusername") || "";
    const msalUsername = localStorage.getItem("msalUsername");

    if (token || msalUsername) {
      set({
        token,
        testvisionusername,
        msalUsername,
        auth: { loading: false, error: null, data: null },
      });
    }
  },
});
